/**
 * Offline Status Indicator Component
 * Shows connection status, offline authentication information, and sync status
 * Requirements: 9.1, 9.2, 9.6, 9.7, 9.8, 9.9
 */

import React, { useState, useEffect } from 'react';
import { useOfflineAuth, useTokenExpirationMonitor } from '../../hooks/useOfflineAuth';
import { enhancedOfflineSync } from '../../services/enhancedOfflineSync';
import { useSyncStore } from '../../store/syncStore';
import { Card } from './Card';
import { Button } from './Button';

interface OfflineIndicatorProps {
  position?: 'top' | 'bottom';
  showDetails?: boolean;
  showSyncStatus?: boolean;
  className?: string;
}

/**
 * Indicator showing offline status, authentication information, and sync status
 */
export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  position = 'bottom',
  showDetails = false,
  showSyncStatus = true,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [syncStatus, setSyncStatus] = useState(enhancedOfflineSync.getSyncStatus());
  
  const {
    isOnline,
    hasValidOfflineToken,
    canAccessReadingHistory,
    accessStatusMessage,
    userEmail,
    tokenExpiresAt,
    refreshAuthStatus
  } = useOfflineAuth();

  const {
    isExpiringSoon,
    timeUntilExpiration,
    shouldShowWarning
  } = useTokenExpirationMonitor();

  const { queue, isSyncing, lastSyncTime } = useSyncStore();

  // Update sync status periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setSyncStatus(enhancedOfflineSync.getSyncStatus());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Handle force sync
  const handleForceSync = async () => {
    if (isOnline && !isSyncing) {
      await enhancedOfflineSync.forceSyncNow();
    }
  };

  // Format last sync time
  const getLastSyncText = () => {
    if (!lastSyncTime) return 'Never';
    
    const now = Date.now();
    const diff = now - lastSyncTime;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    if (minutes < 60) return `${minutes} minutes ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    
    return new Date(lastSyncTime).toLocaleString();
  };

  // Don't show indicator if online and no warnings
  if (isOnline && !shouldShowWarning) {
    return null;
  }

  const getIndicatorColor = () => {
    if (isOnline) return 'bg-green-500';
    if (canAccessReadingHistory && !isExpiringSoon) return 'bg-blue-500';
    if (canAccessReadingHistory && isExpiringSoon) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getBackgroundColor = () => {
    if (isOnline) return 'bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-700';
    if (canAccessReadingHistory && !isExpiringSoon) return 'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-700';
    if (canAccessReadingHistory && isExpiringSoon) return 'bg-yellow-50 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-700';
    return 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700';
  };

  const getTextColor = () => {
    if (isOnline) return 'text-green-800 dark:text-green-200';
    if (canAccessReadingHistory && !isExpiringSoon) return 'text-blue-800 dark:text-blue-200';
    if (canAccessReadingHistory && isExpiringSoon) return 'text-yellow-800 dark:text-yellow-200';
    return 'text-red-800 dark:text-red-200';
  };

  const positionClasses = position === 'top' 
    ? 'top-0 border-b' 
    : 'bottom-0 border-t';

  return (
    <div className={`fixed left-0 right-0 z-50 ${positionClasses} ${getBackgroundColor()} ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center space-x-3">
            {/* Status indicator dot */}
            <div className={`w-2 h-2 rounded-full ${getIndicatorColor()}`} />
            
            {/* Status message */}
            <span className={`text-sm font-medium ${getTextColor()}`}>
              {accessStatusMessage}
            </span>
            
            {/* Expiration warning */}
            {isExpiringSoon && !isOnline && (
              <span className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">
                (Expires in {timeUntilExpiration})
              </span>
            )}

            {/* Sync status */}
            {showSyncStatus && (
              <>
                {isSyncing && (
                  <span className="text-xs text-blue-700 dark:text-blue-300 font-medium flex items-center">
                    <svg className="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Syncing...
                  </span>
                )}
                {!isSyncing && queue.length > 0 && (
                  <span className="text-xs text-orange-700 dark:text-orange-300 font-medium">
                    {queue.length} pending {queue.length === 1 ? 'action' : 'actions'}
                  </span>
                )}
              </>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Force sync button */}
            {isOnline && queue.length > 0 && !isSyncing && (
              <Button
                onClick={handleForceSync}
                variant="ghost"
                size="sm"
                className={`text-xs ${getTextColor()}`}
              >
                Sync Now
              </Button>
            )}

            {/* Refresh button */}
            {!isOnline && (
              <Button
                onClick={refreshAuthStatus}
                variant="ghost"
                size="sm"
                className={`text-xs ${getTextColor()}`}
              >
                Refresh
              </Button>
            )}
            
            {/* Details toggle */}
            {showDetails && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`text-xs ${getTextColor()} hover:opacity-75 transition-opacity`}
              >
                {isExpanded ? 'Hide' : 'Details'}
              </button>
            )}
          </div>
        </div>
        
        {/* Expanded details */}
        {isExpanded && showDetails && (
          <div className={`pb-3 text-xs space-y-1 ${getTextColor()}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <strong>Connection:</strong> {isOnline ? 'Online' : 'Offline'}
              </div>
              
              <div>
                <strong>Authentication:</strong> {hasValidOfflineToken ? 'Valid token' : 'No valid token'}
              </div>
              
              {userEmail && (
                <div>
                  <strong>User:</strong> {userEmail}
                </div>
              )}
              
              {tokenExpiresAt && (
                <div>
                  <strong>Token expires:</strong> {tokenExpiresAt.toLocaleString()}
                </div>
              )}
              
              <div>
                <strong>Reading history:</strong> {canAccessReadingHistory ? 'Accessible' : 'Restricted'}
              </div>

              {showSyncStatus && (
                <>
                  <div>
                    <strong>Sync status:</strong> {isSyncing ? 'Syncing...' : 'Idle'}
                  </div>
                  
                  <div>
                    <strong>Pending actions:</strong> {queue.length}
                  </div>
                  
                  <div>
                    <strong>Last sync:</strong> {getLastSyncText()}
                  </div>
                </>
              )}
              
              {!isOnline && (
                <div className="md:col-span-2">
                  <strong>Note:</strong> Some features are limited while offline. 
                  Connect to the internet for full functionality.
                  {queue.length > 0 && (
                    <> Your changes will sync automatically when you reconnect.</>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Compact offline indicator for use in headers or navigation
 */
export const CompactOfflineIndicator: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { isOnline, canAccessReadingHistory, isTokenExpiringSoon } = useOfflineAuth();
  
  // Don't show if online
  if (isOnline) return null;
  
  const getStatusIcon = () => {
    if (canAccessReadingHistory && !isTokenExpiringSoon) {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      );
    }
    
    if (canAccessReadingHistory && isTokenExpiringSoon) {
      return (
        <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    );
  };
  
  return (
    <div className={`flex items-center space-x-1 ${className}`} title="Offline mode">
      {getStatusIcon()}
      <span className="text-xs text-gray-600 dark:text-gray-400">
        Offline
      </span>
    </div>
  );
};

/**
 * Full-screen offline status overlay for critical situations
 */
export const OfflineStatusOverlay: React.FC<{
  isVisible: boolean;
  onDismiss?: () => void;
}> = ({ isVisible, onDismiss }) => {
  const {
    canAccessReadingHistory,
    shouldShowOfflineWarning,
    accessStatusMessage,
    userEmail,
    tokenExpiresAt
  } = useOfflineAuth();

  if (!isVisible || !shouldShowOfflineWarning) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="max-w-md w-full">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-yellow-600 dark:text-yellow-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" 
              />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Offline Mode Active
          </h2>
          
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <p>{accessStatusMessage}</p>
            
            {userEmail && (
              <p>Logged in as: {userEmail}</p>
            )}
            
            {tokenExpiresAt && (
              <p>Access expires: {tokenExpiresAt.toLocaleString()}</p>
            )}
            
            {!canAccessReadingHistory && (
              <p className="text-red-600 dark:text-red-400">
                Reading history is not accessible offline without valid authentication.
              </p>
            )}
          </div>
          
          {onDismiss && (
            <Button onClick={onDismiss} variant="primary" className="w-full">
              Continue
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OfflineIndicator;